import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';
import { Menu, Bell, User, LogOut, Settings, ChevronDown, Loader2 } from 'lucide-react';

const Header = ({ onMenuClick }) => {
  const { user, userData, logout } = useAuth();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const dropdownRef = useRef(null);
  const notificationsRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
      if (notificationsRef.current && !notificationsRef.current.contains(event.target)) {
        setNotificationsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Carregar contador de notificações não lidas
  useEffect(() => {
    if (user) {
      loadUnreadCount();
      // Atualizar contador a cada 30 segundos
      const interval = setInterval(loadUnreadCount, 30000);
      return () => clearInterval(interval);
    }
  }, [user]);

  const loadUnreadCount = async () => {
    try {
      const getUnreadCount = httpsCallable(functions, 'getUnreadNotificationsCount');
      const result = await getUnreadCount();
      
      if (result.data.success) {
        setUnreadCount(result.data.data.unreadCount);
      }
    } catch (error) {
      console.error('Erro ao carregar contador de notificações:', error);
      setUnreadCount(0);
    }
  };

  const loadNotifications = async () => {
    try {
      setLoadingNotifications(true);
      
      const getNotifications = httpsCallable(functions, 'getNotifications');
      const result = await getNotifications({
        page: 1,
        limit: 10 // Carregar apenas as 10 mais recentes
      });
      
      if (result.data.success) {
        setNotifications(result.data.data.notifications);
        setUnreadCount(result.data.data.unreadCount);
      }
    } catch (error) {
      console.error('Erro ao carregar notificações:', error);
      setNotifications([]);
    } finally {
      setLoadingNotifications(false);
    }
  };

  const handleNotificationClick = async (notification) => {
    try {
      // Marcar como lida se não estiver lida
      if (!notification.read) {
        const markAsRead = httpsCallable(functions, 'markNotificationAsRead');
        await markAsRead({ notificationId: notification.id });
        
        // Atualizar estado local
        setNotifications(prev => 
          prev.map(n => 
            n.id === notification.id 
              ? { ...n, read: true, readAt: new Date().toISOString() }
              : n
          )
        );
        
        // Atualizar contador
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
      
      // Aqui você pode adicionar navegação baseada no tipo de notificação
      handleNotificationAction(notification);
      
    } catch (error) {
      console.error('Erro ao marcar notificação como lida:', error);
    }
  };

  const handleNotificationAction = (notification) => {
    // Navegar baseado no tipo de notificação
    switch (notification.type) {
      case 'document_approved':
      case 'document_rejected':
      case 'document_expiring':
        // Navegar para página de documentos
        window.location.href = '/app/documents';
        break;
      case 'supplier_approved':
      case 'supplier_rejected':
        // Navegar para página de fornecedores
        window.location.href = '/app/fornecedores/ingredientes';
        break;
      case 'new_proposal':
      case 'proposal_response':
        // Navegar para marketplace
        window.location.href = '/app/marketplace';
        break;
      case 'receiving_pending':
        // Navegar para recebimentos
        window.location.href = '/app/receivings';
        break;
      default:
        // Ação padrão ou nenhuma ação
        break;
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      const markAllAsRead = httpsCallable(functions, 'markAllNotificationsAsRead');
      const result = await markAllAsRead();
      
      if (result.data.success) {
        // Atualizar estado local
        setNotifications(prev => 
          prev.map(n => ({ 
            ...n, 
            read: true, 
            readAt: new Date().toISOString() 
          }))
        );
        setUnreadCount(0);
      }
    } catch (error) {
      console.error('Erro ao marcar todas como lidas:', error);
    }
  };

  const handleNotificationsToggle = () => {
    setNotificationsOpen(!notificationsOpen);
    
    // Carregar notificações quando abrir o dropdown
    if (!notificationsOpen && notifications.length === 0) {
      loadNotifications();
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Erro ao fazer logout:', error);
    }
  };

  const getRoleDisplayName = (role) => {
    const roles = {
      superadmin: 'Super Administrador',
      gerente: 'Gerente',
      administrador: 'Administrador',
      recebedor: 'Recebedor',
      fornecedor: 'Fornecedor',
      usuario: 'Usuário'
    };
    return roles[role] || role;
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'document_approved':
        return '✅';
      case 'document_rejected':
        return '❌';
      case 'document_expiring':
        return '⚠️';
      case 'supplier_approved':
        return '🏢✅';
      case 'supplier_rejected':
        return '🏢❌';
      case 'new_proposal':
        return '💼';
      case 'proposal_response':
        return '📝';
      case 'receiving_pending':
        return '📦';
      case 'user_created':
        return '👤';
      case 'system_alert':
        return '🔔';
      default:
        return '📢';
    }
  };

  const getNotificationColor = (type) => {
    switch (type) {
      case 'document_approved':
      case 'supplier_approved':
        return 'bg-green-500';
      case 'document_rejected':
      case 'supplier_rejected':
        return 'bg-red-500';
      case 'document_expiring':
        return 'bg-yellow-500';
      case 'new_proposal':
      case 'proposal_response':
        return 'bg-blue-500';
      case 'receiving_pending':
        return 'bg-purple-500';
      default:
        return 'bg-gray-500';
    }
  };

  const formatNotificationTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Agora';
    if (diffInMinutes < 60) return `${diffInMinutes} min atrás`;
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h atrás`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d atrás`;
    
    return date.toLocaleDateString('pt-BR');
  };

  return (
    <header className="bg-white shadow-sm border-b border-gray-200">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 justify-between items-center">
          {/* Mobile menu button */}
          <button
            type="button"
            className="lg:hidden p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
            onClick={onMenuClick}
          >
            <Menu className="h-6 w-6" />
          </button>

          {/* Logo */}
          <div className="flex items-center flex-1 lg:flex-none">
            <img 
              src="/logo-qualimanager-horizontal.jpeg" 
              alt="QualiManager" 
              className="h-8 w-auto lg:hidden"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'block';
              }}
            />
            <div className="hidden w-8 h-8 bg-blue-900 rounded-lg items-center justify-center lg:hidden">
              <span className="text-white font-bold text-lg">Q</span>
            </div>
          </div>

          {/* Right side */}
          <div className="flex items-center space-x-4">
            {/* Notifications */}
            <div className="relative" ref={notificationsRef}>
              <button
                type="button"
                className="p-2 text-gray-400 hover:text-gray-500 hover:bg-gray-100 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 relative"
                onClick={handleNotificationsToggle}
              >
                <Bell className="h-6 w-6" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 h-5 w-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-medium">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </button>

              {/* Notifications dropdown */}
              {notificationsOpen && (
                <div className="absolute right-0 mt-2 w-96 bg-white rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 z-50 max-h-96 overflow-hidden">
                  <div className="p-4 border-b border-gray-200 flex justify-between items-center">
                    <h3 className="text-lg font-medium text-gray-900">Notificações</h3>
                    {unreadCount > 0 && (
                      <button
                        onClick={handleMarkAllAsRead}
                        className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                      >
                        Marcar todas como lidas
                      </button>
                    )}
                  </div>
                  
                  <div className="max-h-80 overflow-y-auto">
                    {loadingNotifications ? (
                      <div className="p-8 text-center">
                        <Loader2 className="h-6 w-6 animate-spin mx-auto text-gray-400" />
                        <p className="text-sm text-gray-500 mt-2">Carregando notificações...</p>
                      </div>
                    ) : notifications.length === 0 ? (
                      <div className="p-8 text-center">
                        <Bell className="h-12 w-12 text-gray-300 mx-auto" />
                        <p className="text-sm text-gray-500 mt-2">Nenhuma notificação encontrada</p>
                      </div>
                    ) : (
                      notifications.map((notification) => (
                        <div 
                          key={notification.id} 
                          className={`p-4 border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors ${
                            !notification.read ? 'bg-blue-50' : ''
                          }`}
                          onClick={() => handleNotificationClick(notification)}
                        >
                          <div className="flex items-start space-x-3">
                            <div className="flex-shrink-0">
                              <div className={`w-3 h-3 rounded-full mt-2 ${getNotificationColor(notification.type)}`} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between">
                                <p className={`text-sm font-medium ${!notification.read ? 'text-gray-900' : 'text-gray-700'}`}>
                                  {getNotificationIcon(notification.type)} {notification.title}
                                </p>
                                {!notification.read && (
                                  <div className="w-2 h-2 bg-blue-600 rounded-full flex-shrink-0" />
                                )}
                              </div>
                              <p className="text-sm text-gray-600 mt-1">{notification.message}</p>
                              <p className="text-xs text-gray-400 mt-1">
                                {formatNotificationTime(notification.createdAt)}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                  
                  <div className="p-4 border-t border-gray-200">
                    <button 
                      onClick={() => {
                        setNotificationsOpen(false);
                        // Navegar para página de todas as notificações (se existir)
                        // window.location.href = '/app/notifications';
                      }}
                      className="text-sm text-blue-600 hover:text-blue-800 font-medium w-full text-center"
                    >
                      Ver todas as notificações
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* User menu */}
            <div className="relative" ref={dropdownRef}>
              <button
                type="button"
                className="flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                onClick={() => setDropdownOpen(!dropdownOpen)}
              >
                <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full flex items-center justify-center">
                  <span className="text-white text-sm font-medium">
                    {userData?.nome?.charAt(0)?.toUpperCase() || user?.displayName?.charAt(0)?.toUpperCase() || user?.email?.charAt(0)?.toUpperCase()}
                  </span>
                </div>
                <div className="hidden md:block text-left">
                  <p className="text-sm font-medium text-gray-900">{userData?.nome || user?.displayName || user?.email}</p>
                  <p className="text-xs text-gray-500">{getRoleDisplayName(userData?.funcao || user?.funcao || '')}</p>
                </div>
                <ChevronDown className="h-4 w-4 text-gray-400" />
              </button>

              {/* User dropdown */}
              {dropdownOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 z-50">
                  <div className="p-4 border-b border-gray-200">
                    <p className="text-sm font-medium text-gray-900">{userData?.nome || user?.displayName || user?.email}</p>
                    <p className="text-xs text-gray-500">{user?.email}</p>
                    <p className="text-xs text-blue-600 mt-1">{getRoleDisplayName(userData?.funcao || user?.funcao || '')}</p>
                  </div>
                  <div className="py-2">
                    <button className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      <User className="h-4 w-4 mr-3" />
                      Meu Perfil
                    </button>
                    <button className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      <Settings className="h-4 w-4 mr-3" />
                      Configurações
                    </button>
                  </div>
                  <div className="border-t border-gray-200 py-2">
                    <button 
                      onClick={handleLogout}
                      className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                    >
                      <LogOut className="h-4 w-4 mr-3" />
                      Sair
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
